<script setup>
import { defineEmits, defineProps, watch, reactive, computed } from "vue";
import { fromTableColumnsConfig } from "@/components/program-manager/sessions/components/ExcursionsAndEvents/constants";
import { FlexRender } from "@tanstack/vue-table";
import TrashCan from "@/components/shared/icons/TrashCan.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  useVueTable,
} from "@tanstack/vue-table";
import { cloneDeep } from "lodash";

const props = defineProps({
  selectedOccurrences: {
    type: Array,
    required: false,
  },
  defaultOccurrence: {},
  eventsUpdating: {
    type: Boolean,
  },
  isSessionOwner: {
    type: Boolean
  }
});

const emit = defineEmits(["updateDefault", "removeOccurrence"]);

const removeOccurrence = (val) => {
  emit("removeOccurrence", val);
};

const tableColumnsConfig = computed(() => {
  return props.isSessionOwner ? fromTableColumnsConfig : fromTableColumnsConfig.filter((obj) => obj.id !== "price");
});


const columnHelper = createColumnHelper();
const columns = tableColumnsConfig.value.map(
  ({ header, id, size, type, addedClasses, getColumnDisplayValue }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
      type,
      addedClasses,
    });
  }
);

const state = reactive({
  selectedOccurrences: props.selectedOccurrences
    ? cloneDeep(props.selectedOccurrences)
    : [],
});

watch(
  () => props.selectedOccurrences,
  (selectedOccurrences) => {
    state.selectedOccurrences = cloneDeep(selectedOccurrences) || [];
  },
  { deep: true }
);

const table = useVueTable({
  get data() {
    return state.selectedOccurrences || [];
  },
  enableColumnResizing: true,
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getExpandedRowModel: getExpandedRowModel(),
  manualPagination: true,
});
</script>

<template>
  <div class="flex flex-col col-span-2">
    <div class="block text-xs font-semibold text-gray-700">From Library</div>
    <div
      class="grid grid-cols-10 gap-x-2 gap-y-4 items-center bg-white border-gray-300 rounded-md w-full p-4 mt-2"
    >
      <div class="col-span-10">
        <Spinner
          v-if="props.eventsUpdating"
          :show-spinner-content="false"
          outer-classes="flex items-center justify-center bg-white h-full relative"
        />
        <table v-else class="block">
          <thead>
            <tr
              v-for="headerGroup in table.getHeaderGroups()"
              :key="headerGroup.id"
            >
              <th
                v-for="header in headerGroup.headers"
                :key="header.id"
                :colSpan="header.colSpan"
                :style="{ width: `${header.getSize()}px` }"
                class="py-4 text-left text-sm items-center text-gray-900"
              >
                <div
                  class="flex items-center"
                  :class="header.column.columnDef.addedClasses"
                >
                  <FlexRender
                    v-if="!header.isPlaceholder"
                    :render="header.column.columnDef.header"
                    :props="header.getContext()"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody data-testid="excursions-events-library-table-body" class="bg-white">
            <tr
              v-for="row in table.getRowModel().rows"
              :key="row.id"
              class="hover:bg-blue-350"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.column.id"
                :style="{ width: `${cell.column.getSize()}px` }"
                class="py-2 text-sm capitalize"
                :class="cell.column.columnDef.addedClasses"
              >
                <div v-if="cell.column.columnDef.type === 'default_checkbox'">
                  <input
                    type="radio"
                    :value="props.defaultOccurrence === cell.row.original.id"
                    :checked="props.defaultOccurrence === cell.row.original.id"
                    class="h-4 w-4 border-gray-300 form-radio"
                    @input="() => setClassesDefault(cell.row.original.id)"
                  />
                </div>
                <div v-if="cell.column.columnDef.type === 'trash'">
                  <TrashCan
                    class="w-[18px] m-auto cursor-pointer"
                    @click="removeOccurrence(cell.row.original.id)"
                  />
                </div>
                <div v-if="cell.column.columnDef.header === 'Event'">
                  <a
                    class="text-teal-900"
                    :href="`/program-manager/events/${row.original.event_id}`"
                    target="_blank"
                    >{{ row?.original?.title || "" }}</a
                  >
                </div>
                <FlexRender
                  v-else
                  :render="cell.column.columnDef.cell"
                  :props="cell.getContext()"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
